import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Gift, Lock, Unlock, ArrowLeft, Check, X as XIcon, Trash2, Edit3, Plus, RefreshCw, Upload, RotateCcw} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import PageHeader from '@/components/PageHeader'
import { prizesAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { useConfirm } from '../hooks/useConfirm'

export default function PrizesAdminPage() {
  const confirm = useConfirm()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const [tab, setTab] = useState('shop')

  if (!isAdmin) return <div className="p-6 text-muted-foreground">Enkel voor admins.</div>

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        icon={Gift}
        eyebrow="Beheer"
        title="Prijzenkraam admin"
        subtitle="Kraam openen/sluiten, prijzen beheren, claims afhandelen"
        actions={<Button asChild variant="ghost" size="sm"><Link to="/prijzenkraam"><ArrowLeft className="h-4 w-4 mr-1" />Naar kraam</Link></Button>}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="shop">Shop status</TabsTrigger>
          <TabsTrigger value="claims">Claims</TabsTrigger>
          <TabsTrigger value="prizes">Prijzen beheer</TabsTrigger>
          <TabsTrigger value="history">Historiek</TabsTrigger>
        </TabsList>
        <TabsContent value="shop"    className="mt-4"><ShopTab /></TabsContent>
        <TabsContent value="claims"  className="mt-4"><ClaimsTab /></TabsContent>
        <TabsContent value="prizes"  className="mt-4"><PrizesTab /></TabsContent>
        <TabsContent value="history" className="mt-4"><HistoryTab /></TabsContent>
      </Tabs>
    </div>
  )
}

/* ============ Shop tab ============ */
function ShopTab() {
  const [state, setState] = useState(null)
  const [cats, setCats]   = useState([])
  const [busy, setBusy]   = useState(false)
  const load = async () => {
    const [st, cats] = await Promise.all([prizesAPI.getState(), prizesAPI.listCategories()])
    setState(st.data.state)
    setCats(cats.data.categories || [])
  }
  useEffect(() => { load() }, [])
  const toggle = async () => {
    setBusy(true)
    try {
      if (state?.is_open) await prizesAPI.closeShop(); else await prizesAPI.openShop()
      await load()
    } finally { setBusy(false) }
  }
  const setLimit = async (catId, value) => {
    setBusy(true)
    try { await prizesAPI.updateCategory(catId, { max_per_user: Number(value) || 0 }); await load() }
    finally { setBusy(false) }
  }
  if (!state) return <div className="text-muted-foreground text-sm">Laden…</div>
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">Huidige status</div>
              <div className="text-2xl font-bold">
                {state.is_open
                  ? <span className="text-emerald-400">KRAAM OPEN</span>
                  : <span className="text-yellow-500">KRAAM GESLOTEN</span>}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {state.is_open && state.opened_at && `Open sinds ${new Date(state.opened_at).toLocaleString('nl-BE')}`}
                {!state.is_open && state.closed_at && `Gesloten sinds ${new Date(state.closed_at).toLocaleString('nl-BE')}`}
                {!state.opened_at && 'Nog nooit geopend'}
              </div>
            </div>
            <Button
              size="lg" onClick={toggle} disabled={busy}
              className={state.is_open ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 text-black hover:bg-emerald-600'}
            >
              {state.is_open ? <><Lock className="h-4 w-4 mr-2" />Sluit kraam</> : <><Unlock className="h-4 w-4 mr-2" />Open kraam</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <CategoriesBeheer cats={cats} busy={busy} setBusy={setBusy} onReload={load} setLimit={setLimit} />

      <Card className="border-cyan-500/40 bg-cyan-500/5">
        <CardContent className="p-3 text-sm">
          <b>Belangrijk:</b> als de kraam open is kunnen alle users met genoeg punten claimen. Er is geen queue — hoogste punten kies eerst regel enforceer je zelf (of open op afgesproken tijd).
        </CardContent>
      </Card>
    </div>
  )
}

/* ============ Claims tab ============ */
function ClaimsTab() {
  const [claims, setClaims] = useState([])
  const [busy, setBusy] = useState(null)
  const load = async () => {
    const r = await prizesAPI.listClaims()
    setClaims(r.data.claims || [])
  }
  useEffect(() => { load() }, [])

  const handover = async (id) => {
    setBusy(id); try { await prizesAPI.markHandover(id); await load() } finally { setBusy(null) }
  }
  const cancel = async (id) => {
    const claim = claims.find(c => c.id === id)
    const msg = claim?.handed_over_at
      ? `Deze prijs was op ${new Date(claim.handed_over_at).toLocaleDateString('nl-BE')} al opgehaald. Speler geeft hem terug — intrekken? Punten worden teruggeboekt en de prijs komt terug in de pool voor iemand anders.`
      : 'Claim annuleren? Punten worden teruggeboekt en de prijs komt terug in de pool.'
    if (!(await confirm(msg))) return
    setBusy(id); try { await prizesAPI.cancelClaim(id); await load() } finally { setBusy(null) }
  }

  const pending = claims.filter(c => !c.handed_over_at)
  const done    = claims.filter(c =>  c.handed_over_at)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">Nog op te halen ({pending.length})</CardTitle>
          <Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {pending.length === 0 && <p className="text-sm text-muted-foreground">Geen openstaande claims.</p>}
          {pending.map(c => <ClaimRow key={c.id} c={c} busy={busy === c.id} onHandover={() => handover(c.id)} onCancel={() => cancel(c.id)} />)}
        </CardContent>
      </Card>
      {done.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base text-emerald-400">Opgehaald ({done.length})</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {done.map(c => (
              <ClaimRow key={c.id} c={c} compact busy={busy === c.id}
                        onCancel={() => cancel(c.id)} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

/* ============ Historiek tab — volledige audit-log ============ */
const EVENT_META = {
  claim_created:    { label: 'Claim',              color: 'text-acid',       verb: 'claimt' },
  claim_handover:   { label: 'Opgehaald',          color: 'text-emerald-400', verb: 'geeft prijs mee aan' },
  claim_cancelled:  { label: 'Geannuleerd',        color: 'text-signal',      verb: 'annuleert claim van' },
  prize_reset:      { label: 'Reset',              color: 'text-orange-400',  verb: 'reset alle claims op' },
  shop_opened:      { label: 'Shop open',          color: 'text-emerald-400', verb: 'opent de kraam' },
  shop_closed:      { label: 'Shop dicht',         color: 'text-muted-foreground', verb: 'sluit de kraam' },
  prize_created:    { label: 'Prijs +',            color: 'text-live',        verb: 'voegt prijs toe' },
  prize_updated:    { label: 'Prijs ✎',            color: 'text-cyan-400',    verb: 'wijzigt prijs' },
  prize_deleted:    { label: 'Prijs −',            color: 'text-signal',      verb: 'verwijdert prijs' },
  category_created: { label: 'Categorie +',        color: 'text-live',        verb: 'voegt categorie toe' },
  category_updated: { label: 'Categorie ✎',        color: 'text-cyan-400',    verb: 'wijzigt categorie' },
  category_deleted: { label: 'Categorie −',        color: 'text-signal',      verb: 'verwijdert categorie' },
  points_deducted:  { label: '− punten',           color: 'text-signal',      verb: 'punten afgetrokken bij' },
  points_refunded:  { label: '+ punten',           color: 'text-emerald-400', verb: 'punten teruggegeven aan' },
}
const EVENT_TYPES_LIST = Object.keys(EVENT_META)

function HistoryTab() {
  const { user } = useAuth()
  const confirm = useConfirm()
  const [events, setEvents] = useState([])
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  const load = async () => {
    setLoading(true)
    try {
      const r = await prizesAPI.listArchive()
      setEvents(r.data.events || [])
      setTransactions(r.data.transactions || [])
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  // Merge prize_events + points_transactions in één chronologische stream.
  // Elke transactie krijgt een virtueel event_type zodat de filter werkt.
  const merged = [
    ...events.map(e => ({ ...e, __kind: 'event', __at: e.created_at })),
    ...transactions.map(t => ({
      ...t,
      __kind: 'txn',
      __at: t.created_at,
      event_type: t.amount < 0 ? 'points_deducted' : 'points_refunded',
    })),
  ].sort((a, b) => new Date(b.__at) - new Date(a.__at))

  const zichtbaar = filter === 'all' ? merged : merged.filter(e => e.event_type === filter)
  const tellingen = merged.reduce((acc, e) => { acc[e.event_type] = (acc[e.event_type] || 0) + 1; return acc }, {})

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Tijdlijn ({events.length})</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-8 w-48 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle acties</SelectItem>
                {EVENT_TYPES_LIST.filter(t => tellingen[t]).map(t => (
                  <SelectItem key={t} value={t}>
                    {EVENT_META[t].label} ({tellingen[t]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={load}>
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            {user?.role === 'superadmin' && (
              <Button size="sm" variant="outline"
                      className="text-signal border-signal/40 hover:bg-signal/10"
                      onClick={async () => {
                        if (!(await confirm('Volledige historiek wissen? Alle prize_events én prijs-gerelateerde punt-transacties worden weggegooid. Openstaande claims blijven staan. Deze actie kan niet ongedaan gemaakt worden.'))) return
                        try {
                          const r = await prizesAPI.resetHistory()
                          window.alert(`Historiek gewist:\n${r.data.events_deleted} events\n${r.data.transactions_deleted} punt-transacties`)
                          await load()
                        } catch (e) {
                          window.alert(e.response?.data?.error || e.message)
                        }
                      }}>
                <Trash2 className="h-3 w-3 mr-1" />Reset historiek
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-1">
          {zichtbaar.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground">
              {filter === 'all'
                ? 'Nog geen activiteit. Elke actie in de prijzenkraam wordt hier gelogd.'
                : 'Geen events van dit type.'}
            </p>
          )}
          {zichtbaar.map(e => {
            const meta = EVENT_META[e.event_type] || { label: e.event_type, color: 'text-muted-foreground', verb: e.event_type }
            // Punten-transacties uit points_transactions hebben een andere shape.
            if (e.__kind === 'txn') {
              return (
                <div key={`txn-${e.id}`}
                     className="flex items-start gap-3 p-2 rounded border border-border/40 bg-card/50 text-xs">
                  <div className={`pf-mono text-[10px] uppercase tracking-wider w-24 shrink-0 pt-0.5 ${meta.color}`}>
                    {meta.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate">
                      <span className={`font-mono font-semibold ${e.amount >= 0 ? 'text-emerald-400' : 'text-signal'}`}>
                        {e.amount >= 0 ? '+' : ''}{e.amount} pt
                      </span>
                      <span className="text-muted-foreground"> {e.amount >= 0 ? 'teruggegeven aan' : 'afgetrokken bij'} </span>
                      <span className="font-semibold">{e.display_name || e.username}</span>
                      {e.reason && <span className="italic text-muted-foreground"> · {e.reason}</span>}
                    </div>
                    <div className="text-muted-foreground text-[10px]">
                      {new Date(e.created_at).toLocaleString('nl-BE')}
                      {e.created_by_username && e.created_by_username !== e.username && ` · door ${e.created_by_username}`}
                    </div>
                  </div>
                </div>
              )
            }
            return (
              <div key={`evt-${e.id}`}
                   className="flex items-start gap-3 p-2 rounded border border-border/40 bg-card/50 text-xs">
                <div className={`pf-mono text-[10px] uppercase tracking-wider w-24 shrink-0 pt-0.5 ${meta.color}`}>
                  {meta.label}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate">
                    <span className="font-semibold">{e.actor_name || 'systeem'}</span>
                    <span className="text-muted-foreground"> {meta.verb} </span>
                    {e.subject_name && (
                      <span className="font-semibold">{e.subject_name}</span>
                    )}
                    {e.prize_name && (
                      <span className="italic"> · {e.prize_name}</span>
                    )}
                    {e.category_name && !e.prize_name && (
                      <span className="italic"> · {e.category_name}</span>
                    )}
                    {typeof e.points === 'number' && e.points !== 0 && (
                      <Badge variant="outline" className="text-[10px] ml-1">{e.points} pt</Badge>
                    )}
                    {e.data?.was_handed_over && (
                      <Badge className="bg-emerald-500/60 text-black text-[10px] ml-1">was opgehaald</Badge>
                    )}
                    {typeof e.data?.num_claims_refunded === 'number' && (
                      <Badge variant="secondary" className="text-[10px] ml-1">{e.data.num_claims_refunded} refund</Badge>
                    )}
                  </div>
                  <div className="text-muted-foreground text-[10px]">
                    {new Date(e.created_at).toLocaleString('nl-BE')}
                  </div>
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>
    </div>
  )
}

function ClaimRow({ c, busy, onHandover, onCancel, compact }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded border border-border bg-card ${compact ? 'py-2' : ''}`}>
      {c.image_url ? <img src={c.image_url} alt="" className="h-10 w-10 object-cover rounded" /> : <Gift className="h-10 w-10 text-muted-foreground/40" />}
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{c.prize_name} <Badge variant="outline" className="text-xs ml-1">{c.price} pt</Badge></div>
        <div className="text-xs text-muted-foreground truncate">
          {c.display_name || c.username} · {new Date(c.claimed_at).toLocaleString('nl-BE')}
        </div>
        {c.notes && <div className="text-xs text-muted-foreground italic">"{c.notes}"</div>}
      </div>
      {onHandover && (
        <div className="flex gap-1">
          <Button size="sm" onClick={onHandover} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-black">
            <Check className="h-4 w-4 mr-1" />Opgehaald
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} disabled={busy} title="Claim intrekken — punten terug + prijs terug in pool">
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
      {c.handed_over_at && (
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-500 text-black text-xs">✓ {new Date(c.handed_over_at).toLocaleDateString('nl-BE')}</Badge>
          {onCancel && (
            <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}
                    title="Speler brengt prijs terug — punten terug + prijs terug in pool">
              <XIcon className="h-4 w-4 text-muted-foreground hover:text-signal" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/* ============ Prizes tab ============ */
function PrizesTab() {
  const [data, setData] = useState(null)
  const [dialog, setDialog] = useState(null)
  const load = async () => { const r = await prizesAPI.listCategories(); setData(r.data) }
  useEffect(() => { load() }, [])

  const openNew  = () => setDialog({ category_id: data?.categories?.[0]?.id ?? '', name: '', description: '', stock: 1, display_order: 0, imageFile: null })
  const openEdit = (p) => setDialog({ id: p.id, category_id: p.category_id, name: p.name, description: p.description ?? '', stock: p.stock, display_order: p.display_order, imageFile: null, existingImage: p.image_url })

  const save = async (e) => {
    e.preventDefault()
    const payload = {
      category_id: Number(dialog.category_id), name: dialog.name.trim(),
      description: dialog.description || null,
      stock: Number(dialog.stock), display_order: Number(dialog.display_order) || 0,
    }
    let id = dialog.id
    if (id) await prizesAPI.updatePrize(id, payload)
    else {
      const r = await prizesAPI.createPrize(payload); id = r.data.id
    }
    if (dialog.imageFile) await prizesAPI.uploadImage(id, dialog.imageFile)
    setDialog(null); await load()
  }
  const del = async (id) => {
    if (!(await confirm('Prijs verwijderen? Bestaande claims blijven bestaan via cascade.'))) return
    await prizesAPI.deletePrize(id); await load()
  }
  const resetClaims = async (prize) => {
    if (!(await confirm(`Alle claims op "${prize.name}" intrekken? Punten worden teruggestort aan elke betrokken speler, en stock (${prize.stock}) staat weer volledig open.`))) return
    await prizesAPI.resetClaims(prize.id); await load()
  }

  if (!data) return <div className="text-muted-foreground text-sm">Laden…</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={openNew}><Plus className="h-4 w-4 mr-1" />Nieuwe prijs</Button></div>
      {data.categories.map(cat => (
        <Card key={cat.id}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{cat.name} <Badge variant="outline" className="ml-2">{cat.price} pt</Badge></CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {cat.prizes.length === 0 && <p className="text-xs text-muted-foreground">Geen prijzen in deze categorie</p>}
            {cat.prizes.map(p => (
              <div key={p.id} className="flex items-center gap-3 p-2 rounded border border-border">
                {p.image_url ? <img src={p.image_url} alt="" className="h-10 w-10 object-cover rounded" /> : <Gift className="h-10 w-10 text-muted-foreground/40" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    Stock {p.stock} · Beschikbaar {p.remaining}{p.description ? ` · ${p.description.slice(0, 60)}` : ''}
                  </div>
                </div>
                <Button size="sm" variant="ghost" title="Reset claims (stock terugzetten)" onClick={() => resetClaims(p)}><RotateCcw className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Edit3 className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(p.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {dialog && (
        <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{dialog.id ? 'Prijs bewerken' : 'Nieuwe prijs'}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div><Label>Categorie</Label>
                <Select value={String(dialog.category_id)} onValueChange={v => setDialog({ ...dialog, category_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {data.categories.map(c => <SelectItem key={c.id} value={String(c.id)}>{c.name} ({c.price} pt)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Naam *</Label><Input value={dialog.name} onChange={e => setDialog({...dialog, name: e.target.value})} required /></div>
              <div><Label>Beschrijving</Label><Input value={dialog.description} onChange={e => setDialog({...dialog, description: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Stock</Label><Input type="number" min="0" value={dialog.stock} onChange={e => setDialog({...dialog, stock: e.target.value})} /></div>
                <div><Label>Volgorde</Label><Input type="number" value={dialog.display_order} onChange={e => setDialog({...dialog, display_order: e.target.value})} /></div>
              </div>
              <div>
                <Label>Afbeelding (max 3MB)</Label>
                <Input type="file" accept="image/*" onChange={e => setDialog({...dialog, imageFile: e.target.files?.[0]})} />
                {dialog.existingImage && !dialog.imageFile && <img src={dialog.existingImage} alt="" className="h-16 w-16 object-contain mt-2 rounded" />}
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialog(null)}>Annuleer</Button>
                <Button type="submit">{dialog.id ? 'Opslaan' : 'Aanmaken'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

/* Categorieën-beheer: max-per-user aanpassen, hernoemen, prijs wijzigen,
   nieuwe aanmaken, verwijderen. CRUD-endpoints bestonden al in de backend
   (superadmin-gated) — dit is puur de UI. */
function CategoriesBeheer({ cats, busy, setBusy, onReload, setLimit }) {
  const confirm = useConfirm()
  const { user } = useAuth()
  const magCRUD = user?.role === 'superadmin'
  const [dlg, setDlg] = useState(null)

  const save = async () => {
    setBusy(true)
    try {
      const payload = {
        name: dlg.name.trim(),
        price: Number(dlg.price) || 0,
        display_order: Number(dlg.display_order) || 0,
        max_per_user: Number(dlg.max_per_user) || 0,
      }
      if (!payload.name) { window.alert('Naam is verplicht'); return }
      if (dlg.id) await prizesAPI.updateCategory(dlg.id, payload)
      else       await prizesAPI.createCategory(payload)
      setDlg(null)
      await onReload()
    } catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  const wis = async (c) => {
    if (!(await confirm(`Categorie "${c.name}" verwijderen? Kan alleen als er geen prijzen meer in deze categorie zitten.`))) return
    setBusy(true)
    try { await prizesAPI.deleteCategory(c.id); await onReload() }
    catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Categorieën</CardTitle>
            <p className="text-xs text-muted-foreground">
              Prijs per categorie (aantal punten), max-per-user limiet en volgorde.
              {!magCRUD && ' Aanmaken/verwijderen alleen door superadmin.'}
            </p>
          </div>
          {magCRUD && (
            <Button size="sm"
                    onClick={() => setDlg({ name: '', price: 100, display_order: 0, max_per_user: 0 })}>
              <Plus className="h-3.5 w-3.5 mr-1" />Nieuwe categorie
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {cats.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center italic">
              Nog geen categorieën. {magCRUD && 'Klik "+ Nieuwe categorie" om te beginnen.'}
            </p>
          )}
          {cats.map(c => (
            <div key={c.id} className="flex items-center gap-3 p-2 rounded border border-border">
              <Badge variant="outline" className="tabular-nums">{c.price} pt</Badge>
              <div className="flex-1 font-semibold">{c.name}</div>
              <Label className="text-xs text-muted-foreground">Max per user:</Label>
              <Input type="number" min="0" className="w-20"
                     defaultValue={c.max_per_user ?? 0}
                     onBlur={(e) => setLimit(c.id, e.target.value)}
                     disabled={busy} />
              <span className="text-xs text-muted-foreground w-24 text-right">
                {(c.max_per_user ?? 0) === 0 ? 'onbeperkt' : `max ${c.max_per_user}`}
              </span>
              {magCRUD && (
                <>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                          onClick={() => setDlg({
                            id: c.id, name: c.name, price: c.price,
                            display_order: c.display_order ?? 0,
                            max_per_user: c.max_per_user ?? 0,
                          })}
                          title="Aanpassen">
                    <Edit3 className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                          onClick={() => wis(c)} title="Verwijderen">
                    <Trash2 className="h-4 w-4 text-signal" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Dialog: create/edit categorie */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dlg?.id ? 'Categorie aanpassen' : 'Nieuwe categorie'}</DialogTitle>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label>Naam</Label>
                <Input value={dlg.name}
                       onChange={e => setDlg({ ...dlg, name: e.target.value })}
                       placeholder="bv. Kleine prijs" />
              </div>
              <div><Label>Prijs (punten)</Label>
                <Input type="number" min="0" value={dlg.price}
                       onChange={e => setDlg({ ...dlg, price: e.target.value })} />
              </div>
              <div><Label>Volgorde</Label>
                <Input type="number" value={dlg.display_order}
                       onChange={e => setDlg({ ...dlg, display_order: e.target.value })} />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Lager = eerder in de lijst (op de publieke shop-pagina).
                </p>
              </div>
              <div><Label>Max per user</Label>
                <Input type="number" min="0" value={dlg.max_per_user}
                       onChange={e => setDlg({ ...dlg, max_per_user: e.target.value })} />
                <p className="text-[10px] text-muted-foreground mt-1">0 = onbeperkt.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg(null)} disabled={busy}>Annuleer</Button>
            <Button onClick={save} disabled={busy || !dlg?.name?.trim()}>
              {dlg?.id ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
